
/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import { pageContainer, pageHorizontalPadding } from 'src/components/core/layout/page-section';
import { useTranslate } from 'src/core/utils/translator';
import NewsletterCtaForm from './newsletter-cta-form';
import React, { ReactElement, useCallback, useRef, useState } from 'react';
import SubscribeNewsletterSidebar from './subscribe-newsletter-sidebar';
import Type from 'src/components/core/typography/index';
import styled from 'styled-components';

/**
 * Actions width.
 *
 * The form column is 513px wide in the design; it shrinks below that rather
 * than pushing the title.
 */

const actionsWidth = '513px';

/**
 * Title id.
 *
 * The section sits between `main` and `footer`, so it needs an accessible name
 * to count as a landmark; without it its content belongs to no region at all.
 */

const titleId = 'newsletter-cta-title';

/**
 * `Section` styled component.
 */

const Section = styled.section`
  ${pageHorizontalPadding}

  background-color: ${color('beige100')};

  /*
   * Several pages end on this same beige, which left no edge at all between
   * the page and the call to action. The hairline is the divider of the design
   * system, the same one the navbar uses.
   */
  border-top: 1px solid ${color.transparentize('neutralDarkest', 0.15)};
  padding-bottom: ${units(8)};
  padding-top: ${units(8)};

  ${media.min('ms')`
    padding-bottom: ${units(14)};
    padding-top: ${units(14)};
  `}

  @media print {
    display: none;
  }
`;

/**
 * `Container` styled component.
 */

const Container = styled.div`
  ${pageContainer}

  display: flex;
  flex-direction: column;
  gap: ${units(5)};

  ${media.min('ms')`
    align-items: flex-end;
    flex-direction: row;
    justify-content: space-between;
  `}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(2)};

  ${media.min('ms')`
    flex: 1 1 0;
    max-width: ${units(73.75)};
    min-width: 0;
  `}
`;

/**
 * `Tagline` styled component.
 */

const Tagline = styled(Type.Paragraph)`
  color: ${color('brown400')};
  font-style: italic;
  margin: 0;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H4).attrs({ as: 'h2' })`
  letter-spacing: -0.01em;
  line-height: 1.3;
  margin: 0;
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  width: 100%;

  ${media.min('ms')`
    flex: 0 1 ${actionsWidth};
    min-width: 0;
  `}
`;

/**
 * `NewsletterCta` component.
 *
 * Subscribing takes two steps: the email is collected here and the remaining
 * fields in the sidebar, which opens already filled in. Keeping the sidebar
 * means the api contract is untouched.
 */

const NewsletterCta = (): ReactElement => {
  const { translate } = useTranslate();
  const [email, setEmail] = useState<string>('');
  const [isOpenSidebar, setIsOpenSidebar] = useState<boolean>(false);
  const [resetSignal, setResetSignal] = useState<number>(0);
  const submitRef = useRef<HTMLButtonElement>(null);
  const handleContinue = useCallback((value: string) => {
    setEmail(value);
    setIsOpenSidebar(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpenSidebar(false);
    submitRef.current?.focus();
  }, []);

  const handleSuccess = useCallback(() => {
    setEmail('');
    setResetSignal(value => value + 1);
  }, []);

  return (
    <Section aria-labelledby={titleId}>
      <Container>
        <Content>
          <Tagline>
            {translate('footer:newsletter.cta.tagline')}
          </Tagline>

          <Title id={titleId}>
            {translate('footer:newsletter.cta.title')}
          </Title>
        </Content>

        <Actions>
          <NewsletterCtaForm
            onContinue={handleContinue}
            ref={submitRef}
            resetSignal={resetSignal}
          />
        </Actions>
      </Container>

      <SubscribeNewsletterSidebar
        defaultEmail={email}
        isOpen={isOpenSidebar}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
    </Section>
  );
};

/**
 * Export `NewsletterCta` component.
 */

export default NewsletterCta;
